/**
 * Process-wide registry of live AHP relays, keyed by task id. Ensures all
 * consumers for the same task share ONE websocket + one event sink, ref-counted
 * so the connection is torn down only after the last consumer releases it (with
 * a short grace period to survive remounts).
 *
 * Also acts as the single source of truth for "is a relay active for this
 * task", so every consumer can disable its fallback data source consistently.
 *
 * The sink that receives projected events and the Mission Control fetcher are
 * injected by the consumer, keeping this package free of any host application's
 * data layer.
 */

import type {
  ActionEnvelope,
  ChatState,
  MessageAttachment,
  ModelSelection,
  SessionActiveClient,
  SessionState,
  ToolCallResult,
} from '@microsoft/agent-host-protocol'
import {
  RelayConnectionRuntime,
  type ClientTool,
  type RelayLogger,
  type RelayStatus,
  type RelayCreateSessionOptions,
  type RelayConnectionRuntimeOptions,
  type RelayChatObserver,
  type RelayModelCallFailure,
} from './relay-connection-runtime'
import type {ProvisioningStatus} from './session-host/resolve-project'
import type {BuildInputCompletedActionParams} from './ahp-input-actions'
import type {ResourceTokenProvider} from './protected-resource-auth'
import type {MCConnectResponse, MCFetcher} from './mission-control/mission-control-environment-client'
import type {WebSocketFactory} from './webpubsub/wps-transport'
import {buildUserMessage} from './pending-message-actions'
import {
  reconcileRelaySteeringSnapshot,
  recordRelaySteeringMessage,
  reduceRelaySteeringAction,
  type RelaySteeringMessage,
} from './relay-steering-messages'

const DISPOSE_GRACE_MS = 5000

/**
 * Surfaces relay connection diagnostics to the browser console. The runtime
 * otherwise swallows connect/subscribe failures behind a noop logger, which
 * makes a silently-reconnecting relay impossible to debug from the page.
 */
const relayLogger: RelayLogger = {
  // eslint-disable-next-line no-console
  info: message => console.info(`[ahp-relay] ${message}`),
  // eslint-disable-next-line no-console
  error: message => console.error(`[ahp-relay] ${message}`),
}

/** Receives the relay's snapshot + live actions for one session. */
export interface RelaySessionSink {
  /** Take ownership of the consumer's event store before any events arrive. */
  start(): void
  applySnapshot(state: ChatState): void
  applyAction(env: ActionEnvelope): void
  dispose(): void
}

/**
 * A {@link RelaySessionSink} that also exposes its folded `ChatState` as an
 * external store, so consumers can bind to it with `useSyncExternalStore`.
 */
export interface ObservableRelaySink extends RelaySessionSink {
  subscribe(listener: () => void): () => void
  getSnapshot(): ChatState | undefined
}

interface RelayEntry {
  refCount: number
  runtime: RelayRuntimeHandle
  sink: RelaySessionSink
  sessionId: string
  environmentId: string
  channel?: string
  /** Whether the runtime was built to create-or-attach its daemon session. */
  hasCreateSession: boolean
  status: RelayStatus
  /** Sticky once the relay has established at least once, so a woken host stays
   * reachable through brief post-connect `reconnecting` churn (until torn down). */
  hasConnected: boolean
  passive: boolean
  passiveKnown: boolean
  error?: string
  provisioning?: ProvisioningStatus
  modelFailures?: Record<string, RelayModelCallFailure>
  sessionState?: SessionState
  steeringMessages: readonly RelaySteeringMessage[]
  disposeTimer?: ReturnType<typeof setTimeout>
  /** Optimistic auto-approve ("allow all") mirror; the host emits no echo. */
  approveAll: boolean
  /** Guards the connect-time default so the allow-all RPC fires at most once. */
  approveAllApplied: boolean
}

const entries = new Map<string, RelayEntry>()
const listeners = new Set<() => void>()
const EMPTY_STEERING_MESSAGES: readonly RelaySteeringMessage[] = []

export type RelayRuntimeHandle = Pick<RelayConnectionRuntime, 'connect' | 'dispatchTurn'> &
  Partial<
    Pick<
      RelayConnectionRuntime,
      | 'cancelTurn'
      | 'confirmToolCall'
      | 'completeInput'
      | 'completeToolCall'
      | 'dispose'
      | 'ensureLive'
      | 'getClientId'
      | 'queueMessage'
      | 'refresh'
      | 'registerTool'
      | 'removeQueuedMessage'
      | 'steerMessage'
      | 'unregisterTool'
      | 'publishActiveClient'
      | 'renameSession'
      | 'observeChat'
      | 'setSessionApproveAll'
    >
  >

export type RelayRuntimeFactory = (options: RelayConnectionRuntimeOptions) => RelayRuntimeHandle

function notify(): void {
  for (const listener of listeners) listener()
}

export function subscribeRelayRegistry(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function isRelayActive(taskId: string | undefined): boolean {
  if (!taskId) return false
  // An entry that is still in its dispose grace period continues to own the
  // shared store, so consumers must keep their fallback source disabled until
  // it is actually torn down (avoids two sources writing the same key).
  return entries.has(taskId)
}

export function getRelayStatus(taskId: string | undefined): RelayStatus | 'idle' {
  if (!taskId) return 'idle'
  return entries.get(taskId)?.status ?? 'idle'
}

// Reachable = connected now, or connected earlier and merely reconnecting. A
// terminal error/closed relay is not reachable even if it once connected.
export function getRelayReachable(taskId: string | undefined): boolean {
  if (!taskId) return false
  const entry = entries.get(taskId)
  if (!entry) return false
  return entry.status === 'connected' || (entry.hasConnected && entry.status === 'reconnecting')
}

export function getRelayError(taskId: string | undefined): string | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.error
}

export function isRelayPassive(taskId: string | undefined): boolean {
  if (!taskId) return false
  return entries.get(taskId)?.passive ?? false
}

/** Latest repo-project provisioning status for `taskId`, or `undefined` when none is underway. */
export function getRelayProvisioning(taskId: string | undefined): ProvisioningStatus | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.provisioning
}

/** Model-call failures for `taskId` keyed by turn id; persists per turn so a retried turn keeps its error. */
export function getRelayModelFailures(taskId: string | undefined): Record<string, RelayModelCallFailure> | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.modelFailures
}

/** Optimistic auto-approve ("allow all") mirror for `taskId`; false when no relay is active. */
export function getRelayApproveAll(taskId: string | undefined): boolean {
  if (!taskId) return false
  return entries.get(taskId)?.approveAll ?? false
}

/** Page-local steering history for `taskId`, retained until its relay entry is disposed. */
export function getRelaySteeringMessages(taskId: string | undefined): readonly RelaySteeringMessage[] {
  if (!taskId) return EMPTY_STEERING_MESSAGES
  return entries.get(taskId)?.steeringMessages ?? EMPTY_STEERING_MESSAGES
}

/**
 * Returns the live observable store for `taskId`, if the registered sink
 * exposes one.
 */
export function getRelayStore(taskId: string | undefined): ObservableRelaySink | undefined {
  if (!taskId) return undefined
  const sink = entries.get(taskId)?.sink
  return sink && 'getSnapshot' in sink ? (sink as ObservableRelaySink) : undefined
}

export function getRelaySessionState(taskId: string | undefined): SessionState | undefined {
  if (!taskId) return undefined
  return entries.get(taskId)?.sessionState
}

export function observeRelayChat(taskId: string | undefined, channel: string, observer: RelayChatObserver): () => void {
  if (!taskId) throw new Error('no active relay for this task')
  const runtime = entries.get(taskId)?.runtime
  if (!runtime) throw new Error('no active relay for this task')
  if (!runtime?.observeChat) throw new Error('relay does not support additional chat subscriptions')
  return runtime.observeChat(channel, observer)
}

export interface AcquireRelayOptions {
  taskId: string
  environmentId: string
  sessionId: string
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: string
  /** Performs Mission Control requests for connect/reconnect. */
  fetcher: MCFetcher
  /** Builds the sink that receives this relay's snapshot + live actions. */
  createSink: (taskId: string) => RelaySessionSink
  /**
   * Opens the websocket used by the underlying WPS transport. Applied only when
   * constructing a new relay runtime; reused entries keep their original
   * websocket factory.
   */
  webSocketFactory?: WebSocketFactory
  /**
   * Observes relay status changes for host-app diagnostics. Applied only when
   * constructing a new relay runtime; reused entries keep their original
   * status observer.
   */
  onStatus?: (status: RelayStatus, error?: string) => void
  /**
   * Optional pre-obtained `/connect` response, consumed once on first
   * establish instead of making a fresh `/connect` call. Used by flows that
   * already minted credentials so the
   * websocket is opened against the original `client_id`'s WPS groups — a
   * second `/connect` would mint a new client_id and miss events the daemon
   * publishes to the first lane. Subsequent reconnects always use
   * `/reconnect?client_id=…` regardless.
   */
  preconnect?: MCConnectResponse
  // When set, the relay issues `createSession` once on first establish before
  // subscribing. Mutually exclusive in practice with `preconnect`.
  createSession?: RelayCreateSessionOptions
  /** Initial client tools to seed the runtime with. Can be extended dynamically via registerRelayTool. */
  clientTools?: ClientTool[]
  /** Resolves per-resource tokens when the host requires authentication. */
  resourceTokenProvider?: ResourceTokenProvider
  /** Test seam for the underlying relay runtime. Defaults to RelayConnectionRuntime. */
  createRuntime?: RelayRuntimeFactory
}

export function acquireRelay({
  taskId,
  environmentId,
  sessionId,
  channel,
  fetcher,
  createSink,
  webSocketFactory,
  onStatus,
  preconnect,
  createSession,
  clientTools,
  resourceTokenProvider,
  createRuntime,
}: AcquireRelayOptions): Promise<void> {
  const existing = entries.get(taskId)
  if (existing) {
    // The registry is keyed by task id, but the relay is scoped to a specific
    // session + environment + channel. If any differs, the cached runtime points
    // at the wrong channel, so tear it down and recreate rather than reuse.
    const identityMatch =
      existing.sessionId === sessionId && existing.environmentId === environmentId && existing.channel === channel
    // An observe acquire that first ran without createSession (sandbox kind not
    // yet resolved) must rebuild into a create-capable runtime once known —
    // unless the attach already connected, in which case the session exists.
    const needsCreateUpgrade = !!createSession && !existing.hasCreateSession && existing.status !== 'connected'
    if (identityMatch && !needsCreateUpgrade) {
      if (existing.disposeTimer) {
        clearTimeout(existing.disposeTimer)
        existing.disposeTimer = undefined
      }
      existing.refCount += 1
      return Promise.resolve()
    }
    if (existing.disposeTimer) clearTimeout(existing.disposeTimer)
    entries.delete(taskId)
    existing.sink.dispose()
    void existing.runtime.dispose?.()
  }

  const sink = createSink(taskId)
  const entry: RelayEntry = {
    refCount: 1,
    sink,
    sessionId,
    environmentId,
    channel,
    hasCreateSession: !!createSession,
    status: 'idle',
    hasConnected: false,
    passive: false,
    passiveKnown: false,
    approveAll: false,
    approveAllApplied: false,
    steeringMessages: EMPTY_STEERING_MESSAGES,
    runtime: undefined as unknown as RelayRuntimeHandle,
  }

  // Hand cache ownership to the sink before any events arrive.
  sink.start()

  // When pre-seeded, the runtime's first establish reuses the supplied
  // response instead of calling /connect. The lambda is consumed at most
  // once: any later reconnect cycle goes through mcReconnect (default uses
  // the fetcher + the original client_id), so the URL change is automatic.
  const mcConnect = preconnect ? () => Promise.resolve(preconnect) : undefined

  const runtimeOptions: RelayConnectionRuntimeOptions = {
    environmentId,
    sessionId,
    fetcher,
    logger: relayLogger,
    ...(channel ? {channel} : {}),
    ...(mcConnect ? {mcConnect} : {}),
    ...(webSocketFactory ? {webSocketFactory} : {}),
    ...(createSession ? {createSession} : {}),
    ...(clientTools ? {clientTools} : {}),
    ...(resourceTokenProvider ? {resourceTokenProvider} : {}),
    onSnapshot: state => {
      sink.applySnapshot(state)
      const next = reconcileRelaySteeringSnapshot(entry.steeringMessages, state)
      if (next !== entry.steeringMessages) {
        entry.steeringMessages = next
        notify()
      }
    },
    onAction: env => {
      const state = 'getSnapshot' in sink ? (sink as ObservableRelaySink).getSnapshot() : undefined
      const next = reduceRelaySteeringAction(entry.steeringMessages, env, state)
      if (!env.rejectionReason) sink.applyAction(env)
      if (next !== entry.steeringMessages) {
        entry.steeringMessages = next
        notify()
      }
    },
    onStatus: (status, error) => {
      entry.status = status
      if (status === 'connected') entry.hasConnected = true
      entry.error = error ?? (status === 'connecting' || status === 'reconnecting' ? entry.error : undefined)
      // Default-on: every steerable AHP session enables allow-all on its first
      // connect. Passive (read-only) observers are skipped.
      const applyDefaultApproveAll = entry.runtime.setSessionApproveAll?.bind(entry.runtime)
      if (status === 'connected' && !entry.approveAllApplied && !entry.passive && applyDefaultApproveAll) {
        entry.approveAllApplied = true
        entry.approveAll = true
        void (async () => {
          try {
            await applyDefaultApproveAll(true)
          } catch {
            entry.approveAll = false
            notify()
          }
        })()
      }
      onStatus?.(status, error)
      notify()
    },
    onPassiveChange: passive => {
      entry.passive = passive
      entry.passiveKnown = true
      notify()
    },
    onProvisioning: status => {
      entry.provisioning = status
      notify()
    },
    onModelCallFailure: (failure, turnId) => {
      // Replace the map (not mutate) so useSyncExternalStore sees a new snapshot.
      entry.modelFailures = {...(entry.modelFailures ?? {}), [turnId]: failure}
      notify()
    },
    onSessionState: state => {
      entry.sessionState = state
      notify()
    },
  }
  const runtime = createRuntime ? createRuntime(runtimeOptions) : new RelayConnectionRuntime(runtimeOptions)
  entry.runtime = runtime
  entries.set(taskId, entry)
  notify()

  return runtime.connect()
}

/** Dispatch a follow-up user turn over the live relay for a task. */
export function dispatchRelayTurn(
  taskId: string | undefined,
  text: string,
  attachments?: MessageAttachment[],
  model?: ModelSelection,
): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (model) {
    entry.runtime.dispatchTurn(text, attachments, model)
  } else {
    entry.runtime.dispatchTurn(text, attachments)
  }
}

/** Accept, decline, or cancel a pending input request over the live relay. */
export function dispatchRelayInputCompleted(taskId: string | undefined, params: BuildInputCompletedActionParams): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.completeInput) throw new Error('active relay cannot complete input requests')
  entry.runtime.completeInput(params)
}

/** Queue a follow-up user message over the live relay for a task. */
export function queueRelayMessage(
  taskId: string | undefined,
  text: string,
  attachments?: MessageAttachment[],
  model?: ModelSelection,
): string {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.queueMessage) throw new Error('active relay cannot queue messages')
  return entry.runtime.queueMessage(text, attachments, model)
}

/** Steer the active turn over the live relay and retain it in page-local timeline history. */
export function steerRelayMessage(
  taskId: string | undefined,
  text: string,
  attachments?: MessageAttachment[],
  model?: ModelSelection,
  steeringId?: string,
): string {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.steerMessage) throw new Error('active relay cannot steer messages')

  const id = steeringId ?? crypto.randomUUID()
  entry.runtime.steerMessage(text, attachments, model, id)
  const state = getRelayStore(taskId)?.getSnapshot()
  const next = recordRelaySteeringMessage(
    entry.steeringMessages,
    {id, message: buildUserMessage(text, attachments, model)},
    state,
  )
  if (next !== entry.steeringMessages) {
    entry.steeringMessages = next
    notify()
  }
  return id
}

/** Remove a queued user message over the live relay for a task. */
export function removeRelayQueuedMessage(taskId: string | undefined, id: string): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.removeQueuedMessage) throw new Error('active relay cannot remove queued messages')
  entry.runtime.removeQueuedMessage(id)
}

/** Toggle per-session auto-approval ("allow all") on the host over the live relay for a task. */
export async function setRelayApproveAll(taskId: string | undefined, enabled: boolean): Promise<void> {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.setSessionApproveAll) throw new Error('active relay cannot toggle allow-all')
  const previous = entry.approveAll
  // Mirror optimistically and mark applied so the connect-time default can't re-fire.
  entry.approveAll = enabled
  entry.approveAllApplied = true
  notify()
  try {
    await entry.runtime.setSessionApproveAll(enabled)
  } catch (err) {
    entry.approveAll = previous
    notify()
    throw err
  }
}

/** Respond to a pending tool-call confirmation over the live relay for a task. */
export function confirmRelayToolCall(
  taskId: string | undefined,
  confirmation: {
    approved: boolean
    toolCallId: string
    turnId: string
    resultConfirmation?: boolean
    selectedOptionId?: string
  },
): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.confirmToolCall) throw new Error('active relay cannot confirm tool calls')
  entry.runtime.confirmToolCall(confirmation)
}

export function publishRelayActiveClient(taskId: string | undefined, activeClient: SessionActiveClient): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.publishActiveClient) throw new Error('active relay cannot publish active clients')
  entry.runtime.publishActiveClient(activeClient)
}

export function getRelayClientId(taskId: string | undefined): string {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (!entry.runtime.getClientId) throw new Error('active relay cannot report its client id')
  return entry.runtime.getClientId()
}

export function renameRelaySession(taskId: string | undefined, title: string): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.renameSession) throw new Error('active relay cannot rename sessions')
  entry.runtime.renameSession(title)
}

export function completeRelayToolCall({
  taskId,
  turnId,
  toolCallId,
  result,
}: {
  taskId: string | undefined
  turnId: string
  toolCallId: string
  result: ToolCallResult
}): void {
  if (!taskId) throw new Error('no active relay for this task')
  const entry = entries.get(taskId)
  if (!entry) throw new Error('no active relay for this task')
  if (entry.passive) throw new Error('relay connection is read-only')
  if (!entry.runtime.completeToolCall) throw new Error('active relay cannot complete tool calls')
  entry.runtime.completeToolCall({turnId, toolCallId, result})
}

/**
 * Cancel the active turn on the live relay for a task. No-op returning `false`
 * when no relay is registered (e.g. a click after teardown).
 */
export function cancelRelayTurn(taskId: string | undefined, turnId?: string): boolean {
  if (!taskId) return false
  const entry = entries.get(taskId)
  if (!entry) return false
  if (entry.passive) return false
  if (!entry.runtime.cancelTurn) return false
  return entry.runtime.cancelTurn(turnId)
}

/** Register a client tool dynamically on the relay for a task. Auto-announces to the host. */
export function registerRelayTool(taskId: string | undefined, tool: ClientTool): void {
  if (!taskId) return
  const entry = entries.get(taskId)
  if (!entry) return
  entry.runtime.registerTool?.(tool)
}

/** Unregister a client tool by name on the relay for a task. Auto-announces to the host. */
export function unregisterRelayTool(taskId: string | undefined, name: string): void {
  if (!taskId) return
  const entry = entries.get(taskId)
  if (!entry) return
  entry.runtime.unregisterTool?.(name)
}

/**
 * Force the relay for `taskId` to drop and re-establish its subscription,
 * so the next `subscribe` returns a fresh snapshot from the daemon. Useful
 * when a new consumer mounts on top of an already-active runtime whose
 * sink's cache may have missed action notifications (e.g. because the
 * previous consumer was torn down mid-stream).
 *
 * No-op if no relay is registered for `taskId`.
 */
export function refreshRelaySnapshot(taskId: string | undefined): void {
  if (!taskId) return
  const entry = entries.get(taskId)
  if (!entry) return
  // Tolerate runtime stubs (e.g. test fakes) that don't implement refresh().
  if (typeof entry.runtime.refresh !== 'function') return
  void entry.runtime.refresh()
}

/**
 * Confirm the relay for `taskId` can still reach the host, reconnecting it if
 * the lane was reclaimed while the tab was idle.
 *
 * Callers should await this before dispatching after a period of inactivity.
 * `onReconnect` runs before a slow reconnect begins. Await
 * {@link waitForConnected} afterwards when this resolves `true`.
 *
 * No-op if no relay is registered for `taskId`.
 *
 * Unlike the read accessors in this module, `taskId` is required. A `false`
 * here reads as "no reconnect was needed, go ahead and dispatch", so silently
 * accepting a missing id would hand back an all-clear for a relay we never
 * looked at.
 */
export async function ensureRelayLive(taskId: string, onReconnect?: () => void): Promise<boolean> {
  const entry = entries.get(taskId)
  if (!entry) return false
  // Tolerate runtime stubs (e.g. test fakes) that don't implement ensureLive().
  if (typeof entry.runtime.ensureLive !== 'function') return false
  return await entry.runtime.ensureLive(onReconnect)
}

/**
 * Resolves once the relay for `taskId` reaches the `connected` state, so
 * callers that hold an `acquireRelay` ref can safely
 * {@link dispatchRelayTurn} immediately after.
 *
 * Transient `error` / `reconnecting` states are tolerated — the runtime
 * automatically retries with backoff (e.g. when subscribing arrives before
 * the daemon has registered the session). `closed` (terminal) or a terminal
 * repo-project provisioning failure reject immediately.
 *
 * Reaching `connected` can take far longer than a plain connect when the relay
 * is provisioning a repo project (catalogue match or clone) before
 * `createSession`, so once provisioning starts the budget expands to
 * `provisioningTimeoutMs`.
 */
export function whenRelayConnected(
  taskId: string | undefined,
  timeoutMs = 30_000,
  provisioningTimeoutMs = 190_000,
): Promise<void> {
  if (!taskId) return Promise.reject(new Error('no active relay for this task'))
  const initial = entries.get(taskId)
  if (!initial) return Promise.reject(new Error('no active relay for this task'))
  if (initial.status === 'connected') return Promise.resolve()

  return new Promise((resolve, reject) => {
    let settled = false
    let timer: ReturnType<typeof setTimeout>
    // Measured from when provisioning first became active, so a late clone still gets its full budget.
    let provisioningStartedAt: number | undefined
    const startedAt = Date.now()

    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe()
      fn()
    }

    const scheduleTimeout = () => {
      clearTimeout(timer)
      const budget =
        provisioningStartedAt !== undefined
          ? provisioningStartedAt + provisioningTimeoutMs - Date.now()
          : startedAt + timeoutMs - Date.now()
      const remaining = Math.max(0, budget)
      const limit = provisioningStartedAt !== undefined ? provisioningTimeoutMs : timeoutMs
      timer = setTimeout(() => finish(() => reject(new Error(`relay did not connect within ${limit}ms`))), remaining)
    }

    const evaluate = () => {
      if (settled) return
      const entry = entries.get(taskId)
      if (!entry) return finish(() => reject(new Error('relay was torn down before connecting')))

      if (entry.provisioning && entry.provisioning.phase !== 'ready') {
        // A terminal clone/match failure rejects immediately rather than waiting out the timeout.
        if (entry.provisioning.phase === 'failed') {
          const message = entry.provisioning.error ?? 'repository project provisioning failed'
          return finish(() => reject(new Error(message)))
        }
        if (provisioningStartedAt === undefined) {
          provisioningStartedAt = Date.now()
          scheduleTimeout()
        }
      }

      if (entry.status === 'connected') return finish(resolve)
      if (entry.status === 'closed') {
        return finish(() => reject(new Error(entry.error ?? 'relay closed before connecting')))
      }
      // `error` and `reconnecting` are transient — keep waiting; the runtime
      // schedules its own retry with backoff and we will see the next
      // `connected` (or `closed`) transition.
    }

    const unsubscribe = subscribeRelayRegistry(evaluate)
    scheduleTimeout()
    evaluate()
  })
}

export function whenRelayPassiveResolved(taskId: string | undefined, timeoutMs = 30_000): Promise<boolean> {
  if (!taskId) return Promise.reject(new Error('no active relay for this task'))
  const initial = entries.get(taskId)
  if (!initial) return Promise.reject(new Error('no active relay for this task'))
  if (initial.passiveKnown) return Promise.resolve(initial.passive)

  return new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(
      () => finish(() => reject(new Error(`relay passivity was not resolved within ${timeoutMs}ms`))),
      timeoutMs,
    )

    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe()
      fn()
    }

    const evaluate = () => {
      const entry = entries.get(taskId)
      if (!entry) return finish(() => reject(new Error('relay was torn down before passivity resolved')))
      if (entry.passiveKnown) return finish(() => resolve(entry.passive))
    }

    const unsubscribe = subscribeRelayRegistry(evaluate)
    evaluate()
  })
}

export function releaseRelay(taskId: string): void {
  const entry = entries.get(taskId)
  if (!entry) return
  entry.refCount -= 1
  if (entry.refCount > 0) return

  entry.disposeTimer = setTimeout(() => {
    const current = entries.get(taskId)
    if (!current || current.refCount > 0) return
    entries.delete(taskId)
    current.sink.dispose()
    void current.runtime.dispose?.()
    notify()
  }, DISPOSE_GRACE_MS)
}

/** Immediately dispose the relay for `taskId`, bypassing the remount grace period. */
export function disposeRelayNow(taskId: string): void {
  const entry = entries.get(taskId)
  if (!entry) return
  if (entry.disposeTimer) clearTimeout(entry.disposeTimer)
  entries.delete(taskId)
  entry.sink.dispose()
  void entry.runtime.dispose?.()
  notify()
}

/** Test-only: force-clear all relays without grace period. */
export function __resetRelayRegistry(): void {
  for (const [, entry] of entries) {
    if (entry.disposeTimer) clearTimeout(entry.disposeTimer)
    entry.sink.dispose()
    void entry.runtime.dispose?.()
  }
  entries.clear()
  notify()
}
